import { FC } from 'react';
import { Descriptions, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import { GENDERS, USER_VERIFY } from 'constants/users';

import { checkPermission } from 'utils/permission';
import { IPatientPageProps } from '../../_types';

const dash = (value?: string | null) => value || '—';

/** Verified / Not verified tag driven by the `confirm` bitmask. */
const VerifyTag: FC<{ bit: number; confirm?: number | null }> = ({
  bit,
  confirm
}) =>
  confirm && checkPermission(bit, confirm) ? (
    <Tag icon={<CheckCircleOutlined />} color="success">
      Verified
    </Tag>
  ) : (
    <Tag icon={<CloseCircleOutlined />} color="error">
      Not verified
    </Tag>
  );

const PersonalInformation: FC<IPatientPageProps> = ({ patient }) => {
  if (!patient) {
    return null;
  }

  const gender = GENDERS.find(item => item.value === patient.gender)?.label;

  return (
    <Descriptions bordered column={2} size="middle">
      <Descriptions.Item label="First name">
        {dash(patient.first_name)}
      </Descriptions.Item>
      <Descriptions.Item label="Last name">
        {dash(patient.last_name)}
      </Descriptions.Item>
      <Descriptions.Item label="Email">
        {patient.email ? (
          <a href={`mailto:${patient.email}`}>{patient.email}</a>
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Email status">
        <VerifyTag bit={USER_VERIFY.email} confirm={patient.confirm} />
      </Descriptions.Item>
      <Descriptions.Item label="Phone">
        {patient.phone ? (
          <a href={`tel:${patient.phone}`}>{patient.phone}</a>
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Phone status">
        <VerifyTag bit={USER_VERIFY.phone} confirm={patient.confirm} />
      </Descriptions.Item>
      <Descriptions.Item label="Gender">{dash(gender)}</Descriptions.Item>
      <Descriptions.Item label="Date of birth">
        {patient.date_of_birth
          ? dayjs(patient.date_of_birth).format(DATE_FORMAT)
          : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Country">
        {dash(patient.country)}
      </Descriptions.Item>
      <Descriptions.Item label="City">{dash(patient.city)}</Descriptions.Item>
      <Descriptions.Item label="Language">
        {dash(patient.lang)}
      </Descriptions.Item>
      <Descriptions.Item label="Timezone">
        {dash(patient.timezone)}
      </Descriptions.Item>
      <Descriptions.Item label="Registered" span={2}>
        {patient.created_at
          ? dayjs(patient.created_at).format(FULL_DATE_FORMAT)
          : '—'}
      </Descriptions.Item>
    </Descriptions>
  );
};

export default PersonalInformation;
